/** @file The bundle's sheets with pixels where known: packed sheets composited, authored ones from images. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { packedSheetPixels } from '@zilo/pose3d-export/sheets/packedSheetPixels';
import type { BundleSheet, ExportPuppet } from '@zilo/pose3d-export/types/bundle';
import type { ExportDeps } from '@zilo/pose3d-export/types/exportDeps';
import type { Image } from '@zilo/pose3d-export/types/imageIoShapes';
import type { PackBlock, PackedBlocks } from '@zilo/pose3d-export/types/packBlock';

/** Pixels of authored sheet `name` from `images[sheet.image]`; a size other than the sheet's throws `VALIDATION`. */
function authoredPixels(name: string, puppet: Puppet, images: NonNullable<ExportDeps['images']>): Image | undefined {
  const sheet = puppet.sheets[name];
  if (sheet === undefined || !Object.hasOwn(images, sheet.image)) return undefined;
  const pixels = images[sheet.image]?.pixels;
  if (pixels !== undefined && (pixels.width !== sheet.size[0] || pixels.height !== sheet.size[1])) {
    throwError(ERROR_CODES.VALIDATION, { sheet: name, expected: sheet.size, actual: [pixels.width, pixels.height] }, {
      path: `/puppet/sheets/${name}`, hint: 'the sheet image must have the sheet size' });
  }
  return pixels;
}

/**
 * One `BundleSheet` per sheet of the resolved puppet, in its order; `pixels` is set for a packed sheet when every
 * placed image has pixels in `deps.images`, for an authored sheet when its image has.
 */
export function bundleSheets(
  puppet: Puppet, resolved: ExportPuppet, blocks: PackBlock[], packed: PackedBlocks, deps: Pick<ExportDeps, 'images'>,
): Record<string, BundleSheet> {
  const images = deps.images ?? {};
  const sheets: Record<string, BundleSheet> = {};
  for (const [name, ref] of Object.entries(resolved.sheets)) {
    const pixels = Object.hasOwn(packed.sheets, name)
      ? packedSheetPixels(name, ref.size, blocks, packed, images)
      : authoredPixels(name, puppet, images);
    sheets[name] = { name, size: [...ref.size], ...(pixels === undefined ? {} : { pixels }) };
  }
  return sheets;
}
